/**
 * Hotel GST Details API
 *
 * Handles GST add/edit/remove operations via CBackend.
 * CBackend forwards to Agent API with api_call_identifier = HOTEL_GST_DETAILS.
 *
 * Backend is the single source of truth - frontend should NEVER derive GST state locally.
 *
 * Operations:
 * - hotel_gst_detail_add: Add GST details (when all 3 fields are filled and valid)
 * - hotel_gst_detail_edit: Edit existing GST details (when fields change after save)
 * - hotel_gst_detail_remove: Remove GST details (when checkbox is unchecked)
 */
import type { GstDetailActionType, GstDetailRequest } from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { generateUUID } from '@/shared/utils/uuid';
import { baseFetch, createApiError, getCBContext } from './baseApi';

// ============================================
// Error Class
// ============================================

export const GstDetailsError = createApiError('GstDetailsError');

// ============================================
// Response Type
// ============================================

export interface GstDetailsApiResponse {
  type: 'hotels-gst-details';
  gstDetails: GstDetailRequest | null;
  /** Whether GST is currently applied - from API response (resources[0].data.isGstApplied) */
  isGstApplied: boolean;
  success: boolean;
  error?: { code: string; message: string };
}

// ============================================
// Request Types
// ============================================

export interface GstDetailsRequestParams {
  /** Session ID */
  sessionId: string;
  /** User ID */
  userId: string;
  /** Transaction key from review response */
  transactionKey: string;
  /** Action type */
  actionType: GstDetailActionType;
  /** GST details for add/edit operations (null for remove) */
  gstDetails: GstDetailRequest | null;
}

// ============================================
// Raw API Response Types
// ============================================

interface RawCBackendResponse {
  success?: boolean;
  gstDetails?: GstDetailRequest | null;
  isGstApplied?: boolean;
  errorCode?: string;
  errorMessage?: string;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the CBackend request payload for GST details operations
 */
function buildCBackendPayload(params: GstDetailsRequestParams): Record<string, unknown> {
  const context = getCBContext();

  return {
    request_id: generateUUID(),
    conversation_id: generateUUID(),
    uuid: context.uuid,
    auth: context.authToken,
    device_info: {
      device_id: context.deviceId,
      device_type: 'DESKTOP',
      app_ver: '1.21.1',
      platform: 'DESKTOP',
    },
    rephrased_query: getQueryForAction(params.actionType),
    intents: [],
    action_type: params.actionType,
    request_json:
      params.actionType === 'hotel_gst_detail_remove'
        ? { transactionKey: params.transactionKey }
        : {
            transactionKey: params.transactionKey,
            gstDetails: params.gstDetails,
          },
  };
}

/**
 * Get appropriate query string for the action type
 */
function getQueryForAction(actionType: GstDetailActionType): string {
  switch (actionType) {
    case 'hotel_gst_detail_add':
      return 'Add GST details';
    case 'hotel_gst_detail_edit':
      return 'Edit GST details';
    case 'hotel_gst_detail_remove':
      return 'Remove GST details';
    default:
      return 'GST operation';
  }
}

// ============================================
// API Function
// ============================================

/**
 * Call GST details CBackend API for add/edit/remove operations.
 * POST /hotels/gst-details?sessionId=...&userId=...
 *
 * CBackend forwards to Agent API with api_call_identifier = HOTEL_GST_DETAILS.
 *
 * IMPORTANT: The response contains the updated GST details.
 * Frontend MUST override its local state with this response - no local computation.
 */
export async function callGstDetailsApi(
  params: GstDetailsRequestParams,
): Promise<GstDetailsApiResponse> {
  const emptyResponse: GstDetailsApiResponse = {
    type: 'hotels-gst-details',
    gstDetails: null,
    isGstApplied: false,
    success: false,
    error: { code: 'UNKNOWN_ERROR', message: 'Unknown error occurred' },
  };

  const payload = buildCBackendPayload(params);

  logger.warn('[GstDetailsApi] Calling CBackend API:', {
    actionType: params.actionType,
    hasGstDetails: params.gstDetails !== null,
  });

  const { data, error } = await baseFetch<RawCBackendResponse>({
    endpoint: HOTELS_ENDPOINTS.GST_DETAILS,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'GstDetailsApi',
  });

  // Network/HTTP error
  if (error) {
    logger.error('[GstDetailsApi] Network error:', error);
    return { ...emptyResponse, error };
  }

  // API returned error
  if (data?.errorCode ?? data?.errorMessage) {
    const apiError = {
      code: data.errorCode ?? 'API_ERROR',
      message: data.errorMessage ?? 'Unknown API error',
    };
    logger.error('[GstDetailsApi] API error:', apiError);
    return { ...emptyResponse, error: apiError };
  }

  // Check success flag
  if (data?.success === false) {
    const apiError = {
      code: data.errorCode ?? 'OPERATION_FAILED',
      message: data.errorMessage ?? 'GST operation failed',
    };
    logger.error('[GstDetailsApi] Operation failed:', apiError);
    return { ...emptyResponse, error: apiError };
  }

  // Extract GST details and isGstApplied from response
  // Response structure: resources[0].data.gstDetails and resources[0].data.isGstApplied
  const gstDetails = data?.gstDetails ?? null;
  const isGstApplied = data?.isGstApplied ?? false;

  logger.info('[GstDetailsApi] Success:', {
    hasGstDetails: gstDetails !== null,
    isGstApplied,
    gstNumber: gstDetails?.gstNumber,
  });

  return {
    type: 'hotels-gst-details',
    gstDetails,
    isGstApplied,
    success: true,
  };
}

// ============================================
// Convenience Functions
// ============================================

/**
 * Add GST details
 */
export async function addGstDetails(
  params: Omit<GstDetailsRequestParams, 'actionType'>,
): Promise<GstDetailsApiResponse> {
  return callGstDetailsApi({
    ...params,
    actionType: 'hotel_gst_detail_add',
  });
}

/**
 * Edit existing GST details
 */
export async function editGstDetails(
  params: Omit<GstDetailsRequestParams, 'actionType'>,
): Promise<GstDetailsApiResponse> {
  return callGstDetailsApi({
    ...params,
    actionType: 'hotel_gst_detail_edit',
  });
}

/**
 * Remove GST details
 */
export async function removeGstDetails(
  params: Omit<GstDetailsRequestParams, 'actionType' | 'gstDetails'>,
): Promise<GstDetailsApiResponse> {
  return callGstDetailsApi({
    ...params,
    actionType: 'hotel_gst_detail_remove',
    gstDetails: null,
  });
}
